import { useDotLegalQuery } from "@common/hooks/dotLegalReactQuery";
import { SystemProcessingActivitiesViewModel } from "./SystemProcessingActivitiesTab.types.ts";
import { FilterOption, FilterOptionType } from "@common/components/filterPopover/FilterPopover.types.ts";
import { useTranslation } from "@localization/useTranslation.ts";
import { getUniqueFilterOptions, sortByName } from "@common/hooks/filterUtils.ts";
import { useFilterState } from "@common/hooks/useFilterState.ts";
import { applyClientFilters, withOperators } from "@common/components/filterBar/filterOperatorMatching.ts";

export function useSystemProcessingActivitiesTab(systemId: string) {
    const { translateString } = useTranslation();

    const {
        selectFilters: filters,
        filterOperators,
        onFilterChange,
        pinnedFilterIds,
        onPinnedFilterIdsChange,
    } = useFilterState(`system-${systemId}-processing-activities-filters`);

    const url = `/SystemOverview/${systemId}/processing-activities`;
    const { isLoading, data } = useDotLegalQuery<SystemProcessingActivitiesViewModel>(url);

    const allRows = data?.processingActivities ?? [];

    const filteredRows = applyClientFilters(
        allRows,
        [
            { id: "name", candidate: (r) => r.id },
            { id: "owner", candidate: (r) => r.owner },
            { id: "risk", candidate: (r) => (r.riskScore != null && r.riskScore > 0 ? r.riskScore.toString() : "0") },
            { id: "businessArea", candidate: (r) => r.businessAreas.map((a) => a.id) },
        ],
        filters,
        {},
        filterOperators
    )!;

    function getFilterOptions(): Array<FilterOption> {
        return withOperators(
            [
                {
                    id: "name",
                    label: translateString("processingActivity"),
                    type: FilterOptionType.SelectField,
                    value: filters.name ?? [],
                    fixedData: getUniqueFilterOptions(
                        allRows,
                        (r) => r.name,
                        (r) => r.id
                    ).sort(sortByName),
                },
                {
                    id: "owner",
                    label: translateString("responsible"),
                    type: FilterOptionType.SelectField,
                    value: filters.owner ?? [],
                    fixedData: getUniqueFilterOptions(allRows, (r) => r.owner ?? undefined).sort(sortByName),
                },
                {
                    id: "risk",
                    label: translateString("risk"),
                    type: FilterOptionType.SelectField,
                    value: filters.risk ?? [],
                    fixedData: [
                        { id: "1", name: translateString("veryLow") },
                        { id: "2", name: translateString("low") },
                        { id: "3", name: translateString("moderate") },
                        { id: "4", name: translateString("high") },
                        { id: "5", name: translateString("veryHigh") },
                        { id: "0", name: translateString("notAnswered") },
                    ],
                },
                {
                    id: "businessArea",
                    label: translateString("businessAreas"),
                    type: FilterOptionType.SelectField,
                    value: filters.businessArea ?? [],
                    fixedData: getUniqueFilterOptions(
                        allRows.flatMap((r) => r.businessAreas),
                        (a) => a.name,
                        (a) => a.id
                    ).sort(sortByName),
                },
            ],
            filterOperators
        );
    }

    const hasActiveFilters = Object.values(filters).some((v) => v.length > 0);

    return {
        pinnedFilterIds,
        onPinnedFilterIdsChange,
        isLoading,
        hasData: isLoading || allRows.length > 0,
        rows: filteredRows,
        currentRiskAssessmentVersionId: data?.currentRiskAssessmentVersionId,
        getFilterOptions,
        onFilterChange,
        hasActiveFilters,
    };
}
